import { Button } from "@/components/flexkit/Button";
import { Link } from "@/components/flexkit/Link";
import { Pill } from "@/components/podkit/status/Pill";
import { toast } from "@/components/podkit/toasts/use-toast";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Heading1, Heading3 } from "@/components/podkit/typography/Headings";
import { Text as Paragraph } from "@/components/podkit/typography/Text";
import { ENTERPRISE_TRIAL_REQUESTED } from "@/components/tiering/TierViolationDialog";
import { usePreferenceValue } from "@/hooks/use-preference-value";
import { useSegmentTrack } from "@/hooks/use-segment";
import { useGetAccount } from "@/queries/account-queries";
import { KeyIcon, ShieldCheckIcon, UsersIcon } from "lucide-react";
import { type FC, type ReactNode, useCallback } from "react";

export const OrganizationSecretsEducationPage: FC = () => {
    const segmentTrack = useSegmentTrack();
    const { data: account } = useGetAccount();

    const { value: trialRequestedValue, setValueAsync: setTrialRequestedValue } =
        usePreferenceValue(ENTERPRISE_TRIAL_REQUESTED);
    const trialRequested = trialRequestedValue === "true";

    const handleRequestTrial = useCallback(async () => {
        try {
            segmentTrack("trial_requested", { type: "enterprise", email: account?.email });
            await setTrialRequestedValue("true");
            toast({
                title: "Trial requested!",
                description: "Our team is working on your request and will be in touch soon.",
            });
        } catch (error) {
            toast({
                title: "Failed to request trial",
                description: error instanceof Error ? error.message : "An unknown error occurred.",
            });
        }
    }, [account?.email, segmentTrack, setTrialRequestedValue]);

    return (
        <div className="flex flex-col items-center justify-center py-4">
            <div className="w-full max-w-7xl">
                <Pill variant="neutral" size="lg" className="bg-surface-glass font-normal text-content-primary">
                    Available on the Enterprise tier ✨
                </Pill>
                <div className="my-16 flex max-w-full flex-row flex-wrap justify-between gap-8">
                    <div className="max-w-xl">
                        <Heading1 className="mb-4 flex flex-row items-center gap-2">
                            Secure organization-wide secrets management
                        </Heading1>
                        <Paragraph className="text-secondary mb-4 text-balance text-lg">
                            Organization-wide secrets provide a centralized way to manage sensitive information like API
                            keys, tokens, and credentials that are automatically available across all environments in
                            your organization.
                        </Paragraph>
                        <Paragraph className="text-secondary mb-8 text-balance">
                            In the meantime, you can manage{" "}
                            <Link to="/settings/secrets" className="font-medium underline">
                                personal secrets
                            </Link>{" "}
                            that are available in your own environments.
                        </Paragraph>
                        <div className="mt-12 flex flex-col items-center justify-start gap-4 sm:flex-row">
                            <Button
                                size="lg"
                                onClick={handleRequestTrial}
                                disabled={trialRequested}
                                data-tracking-id="request-trial-organization-secrets-education-page"
                            >
                                {trialRequested ? "Trial Requested" : "Request trial"}
                            </Button>
                            <Button variant="outline" size="lg" asChild>
                                <ExternalLink
                                    href="https://ona.com/docs/ona/organizations/organization-secrets"
                                    iconSize="sm"
                                >
                                    Learn more
                                </ExternalLink>
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col gap-8 md:flex-row">
                    <OrganizationSecretsEducationCard
                        icon={<ShieldCheckIcon size={24} />}
                        title="Centralized security management"
                        description="Manage all your organization's secrets in one secure location. No more scattered API keys or credentials across different projects."
                    />

                    <OrganizationSecretsEducationCard
                        icon={<KeyIcon size={24} />}
                        title="Automatic availability across environments"
                        description="Organization secrets are automatically injected into all environments, ensuring consistent access to required credentials without manual setup."
                    />

                    <OrganizationSecretsEducationCard
                        icon={<UsersIcon size={24} />}
                        title="Team collaboration and access control"
                        description="Enable secure collaboration by providing controlled access to shared secrets while maintaining security and audit trails."
                    />
                </div>
            </div>
        </div>
    );
};

const OrganizationSecretsEducationCard: FC<{
    icon?: ReactNode;
    title: string;
    description: string;
}> = ({ icon, title, description }) => {
    return (
        <div className="flex flex-col items-center md:items-start">
            {icon}
            <div className="mt-2 flex flex-row items-center gap-2 text-left">
                <Heading3 className="mb-2">{title}</Heading3>
            </div>
            <Paragraph className="text-left text-content-secondary">{description}</Paragraph>
        </div>
    );
};
